/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'
import { cleanString } from './util'

/*
 * To find supported syntax
 * Note: This will also match mixed syntax, but this is not forseen
 * to be an issue. If it turns out to be, split the regex as:
 *     (^latex:)(.+?)($)|(\$\$)(.+?)(\$\$)
 * and update the replace fn to look at groups 4,5,6 too
 */
const LATEX_REGEX = /(\$\$|^latex:)(.+?)(\$\$|$)/ig

/*
 * Used to find our temporary placeholders
 * Which are used to mark where this inserts HTML
 * So that we can escape other possibly unsafe HTML
 */
const PLACEHOLDER_REGEX = /{{(\d+)}}/ig

const LIB_SCRIPT_URL = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.0/katex.min.js'
const LIB_STYLE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.0/katex.min.css'

const scriptLoad = Backbone.$.Deferred()
const styleLoad = Backbone.$.Deferred()
const librariesLoaded = Backbone.$.when(scriptLoad, styleLoad)
let loadStarted = false

/*
 * Ensurue libs are loaded
 * @return [Promise]
 */
const getLib = function () {
  if (!loadStarted) {
    loadStarted = true
    const head = document.getElementsByTagName('head')[0]

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.onload = scriptLoad.resolve
    script.src = LIB_SCRIPT_URL

    const style = document.createElement('link')
    style.rel = 'stylesheet'
    style.onload = styleLoad.resolve
    style.href = LIB_STYLE_URL

    head.appendChild(style)
    head.appendChild(script)
  }

  return librariesLoaded
}

// Get options for KaTeX based on our options
const katexOpts = opts =>
  ({
    displayMode: !opts.inline, // False = inline
    throwOnError: false,
    errorColor: opts.errorColor
  })

// Render a formula to a HTML string
const renderFormula = function (formula, opts) {
  try {
    return katex.renderToString(formula, katexOpts(opts))
  } catch (error) {
    return '[<em>error rendering eqn.</em>]'
  }
}

// Makes the string safe to render, without stripping HTML added by this lib
const cleanAndReplace = function (text, renderFn, opts) {
  const formulas = []
  let html = text.replace(LATEX_REGEX, function (match, delim, formula) {
    const index = formulas.length
    formulas.push(formula)
    return `{{${index}}}`
  })

  html = cleanString(html, { whitelist: opts.cleanWhitelist })

  return html.replace(PLACEHOLDER_REGEX, (match, index) => renderFn(formulas[index]))
}

// Replace formulas with LaTeX HTML
const formulaToHtml = function (text, opts) {
  if (opts.cleanHTML) {
    return cleanAndReplace(text, formula => renderFormula(formula, opts)
      , opts)
  } else {
    return text.replace(LATEX_REGEX, (match, delim, formula) => renderFormula(formula, opts))
  }
}

// Replace formulas with placeholder text while libs load
const formulaToPlaceholder = function (text, opts) {
  if (opts.cleanHTML) {
    return cleanAndReplace(text, () => '[<em>loading eqn.</em>]'
      , opts)
  } else {
    return text.replace(LATEX_REGEX, '[<em>loading eqn.</em>]')
  }
}

// Strip our formatting (latex: or $$) from the text
const formulaToText = text =>
  text.replace(LATEX_REGEX, (input, delim, formula) => formula)

/*
 * Latex helper
 *
 * - Checks if a value is Latex (according to our formatting rules)
 * - Abstracts the underlying latex library (KaTeX now)
 * - Handles loading the Latex lib in the background
 * - Renders a value containing LaTeX into a DOM element
 * - Includes placeholders while it loads
 *
 * Supported LaTeX syntax in text/strings:
 *
 * - latex:e=mc^2
 * - Text with inline $$x = y^2$$ math!
 *
 * While the dependencies are loading, formulas will be replaced by
 * `[<em>loading eqn.</em>]`, once loaded the LaTeX HTML will be inserted.
 * If there is a minor error in the LaTeX then it will be highlighted
 * `errorColor` in the rendered equation, major errors will cause
 * `[<em>error rendering eqn.</em>]` to be displayed.
 *
 * @note This is a static class, there is no need to instance it
 * @note This is included in the assets prerequisites, so no need to
 * include it manually
 *
 * @example Usage
 * if Util.Latex.isLatex @model.get("value")
 * Util.Latex.renderInto @$(".label_content"), @model.get("value")
 * .then ->
 * Do things, like scale the font size for the LaTeX to fit
 *
 * @namespace Util
 * @mixin
 *
 */
class Latex {
  static initClass () {
    /*
     * *Default options**
     *
     * inline [Boolean]
     * - True if the equation should render inline (default)
     *
     * errorColor [CSS Color String]
     * - Color of errors **within** equations
     * - _Note: errors rendering anything at all, result in separate error text_
     *
     * cleanHTML [Boolean]
     * - True to clean the rest of the string (non-formula) for bad HTML
     *
     * containerClass [String]
     * - Optionally add a class to the internal container
     */
    this.DEFAULT_OPTS = {
      inline: true,
      errorColor: '#cc0000',
      cleanHTML: true,
      cleanWhitelist: [],
      containerClass: ''
    }
  }

  /*
   * Determine if a text value contains LaTeX
   *
   * @param [String] value Value to check
   * @return [Boolean]
   */
  static isLatex (value) {
    LATEX_REGEX.lastIndex = 0
    return LATEX_REGEX.test(value)
  }

  /*
   * Render the value (containing LaTeX) into an element
   * Initially it will render the LaTeX formula(s) with placeholders,
   * then once libs loaded it will render the actual math. The promise
   * completes when the actual math renders.
   *
   * @param [Element|Object] el DOM element or jQuery element
   * @param [String] text Text to process
   * @param [Object] opts The options
   * @return [Promise]
   */
  static renderInto (el, text, opts) {
    if (opts == null) { opts = {} }
    opts = _.defaults(opts, Latex.DEFAULT_OPTS)
    const $el = Backbone.$(el)

    $el.attr('m-latex', '')

    // Re-use the latex container if it's already there
    let $latexContainer = $el.find('.latex--container')
    if ($latexContainer.length === 0) {
      $latexContainer = Backbone.$('<span class="latex--container"></span>')
    }

    if (opts.containerClass) {
      $latexContainer.addClass(opts.containerClass)
    }
    $el.append($latexContainer)

    const promise = Latex._getLib().then(() =>
      $latexContainer.attr('data-rendered', true)
        .html(formulaToHtml(text, opts))
    )

    if (promise.state() === 'pending') {
      $latexContainer.attr('data-rendered', false)
        .html(formulaToPlaceholder(text, opts))
    }

    return promise
  }

  /*
   * Strip out the LaTeX tags from a string
   *
   * @param [String] value Value to check
   * @return [String]
   */
  static getText (text) {
    return formulaToText(text)
  }

  // This is to allow stubbing for tests
  static _getLib () {
    return getLib()
  }
}
Latex.initClass()

export default Latex
